/**
 * Perception and alert (Phase 57 — S0 stealth, turn-based).
 *
 * An optional layer on a match: an **alert ladder per planet** (unaware →
 * suspicious → alert) and a **perception rule** for guards. With the layer
 * absent (`modeState.alert` undefined) nothing here runs and every unit sees
 * exactly what today's line-of-sight rule says — the default is today's game.
 *
 * With the layer on (`modeConfig.startingAlert` set):
 *   - a guard (a unit on a `guardTeams` team; every team when the list is
 *     absent) on a planet that is not yet alert perceives only what is inside
 *     its range and its vision cone (facing, when it has one) — on top of the
 *     terrain line-of-sight rule, never instead of it;
 *   - a guard that perceives an enemy raises its planet: alert when the enemy
 *     is close, suspicious otherwise, remembering where;
 *   - noise raises planets: a shot's impact alerts the planet it hits and makes
 *     planets within earshot suspicious; a beam makes the shooter's planet
 *     suspicious; a takedown is silent;
 *   - levels decay by turns without new cause; `wentLoud` records whether any
 *     planet ever reached alert (the sector's "went loud").
 *
 * Pure: (state, …) → state. Deterministic. No RNG.
 */

import { objectiveProgress } from './modes';
import type { GameState, PlayerState, PlanetState, Vec3, AlertLevel, AlertState, ModeConfig, ScaleProfileName } from '../types';
import { vecNormalize, vecSub, vecDot, vecLength, vecScale, surfaceTangentFrame } from '../math';
import { losEyePoint, hasLineOfSight } from './los';
import { scaleProfile, eyeHeightFor, type Observer } from './scaleProfile';
import { observerOf } from './body';
import {
  PERCEPTION_CONE_HALF_ANGLE, PERCEPTION_RANGE_UNAWARE, PERCEPTION_RANGE_SUSPICIOUS, PERCEPTION_CLOSE_RANGE,
  ALERT_DECAY_TURNS_SUSPICIOUS, ALERT_DECAY_TURNS_ALERT, NOISE_RADIUS_SHOT,
} from '../constants';
import { weatherEffectHere, weatherEffect, WEATHER_EFFECTS } from './weather';
import { cellsAt, weatherTime, gridSecond } from './windField';
import { cellWeatherEffect } from './weatherCells';
import { WEATHER_CELLS } from '../constants/action';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { simWork } from './workCounters';
import { cellOf, cellCentre } from './cells';
import { dcos, dhypot, dsin } from './dmath';

const LEVEL_RANK: Record<AlertLevel, number> = { unaware: 0, suspicious: 1, alert: 2 };

export function alertLayerActive(state: Pick<GameState, 'modeState'>): boolean {
  return state.modeState.alert !== undefined;
}

/** The layer's initial state for a match, or undefined when the match has no alert layer. */
export function initAlertState(config: ModeConfig, planets: PlanetState[]): AlertState | undefined {
  if (!config.startingAlert) return undefined;
  const byPlanet: AlertState['byPlanet'] = {};
  for (let i = 0; i < planets.length; i++) {
    if (planets[i].type === 'planet') byPlanet[i] = { level: config.startingAlert, since: 0 };
  }
  return { byPlanet, wentLoud: config.startingAlert === 'alert' };
}

/** A planet's level; 'alert' when the layer is absent (today's game: everyone always sees). */
export function alertLevelAt(state: Pick<GameState, 'modeState'>, planetIndex: number): AlertLevel {
  const a = state.modeState.alert;
  if (!a) return 'alert';
  return a.byPlanet[planetIndex]?.level ?? 'alert';
}

/**
 * 57.4: the guard teams a match defaults to when the layer is on and none were named — every team with no human
 * on it. In the game that makes the human the infiltrator; in a headless match (all bots) it is every team, which is
 * what an undefined list already meant.
 */
export function botGuardTeams(players: ReadonlyArray<Pick<PlayerState, 'teamIndex' | 'isBot'>>): number[] {
  const teams = [...new Set(players.map(p => p.teamIndex))].sort((a, b) => a - b);
  return teams.filter(t => players.every(p => p.teamIndex !== t || p.isBot));
}

/**
 * 57.7c: stamp each team's objective progress and the turn it last changed (AlertState.progress). Pure; a
 * turn-start step with the layer on. The objective-aware clock reads `turn - since`.
 */
export function tickProgress(state: GameState, turn: number): GameState {
  const alert = state.modeState.alert;
  if (!alert) return state;
  const prev = alert.progress ?? {};
  const progress: Record<number, { value: number; since: number }> = {};
  for (const t of new Set(state.players.map(p => p.teamIndex))) {
    const value = objectiveProgress(state, t);
    const was = prev[t];
    progress[t] = was && was.value === value ? was : { value, since: turn };
  }
  return { ...state, modeState: { ...state.modeState, alert: { ...alert, progress } } };
}

/** 57.7c: turns since this team's objective progress last changed (0 with no ledger yet). */
export function stalledTurns(state: GameState, teamIndex: number): number {
  const p = state.modeState.alert?.progress?.[teamIndex];
  return p ? Math.max(0, state.turnNumber - p.since) : 0;
}

/** Is this unit a guard — one whose perception the layer limits? */
export function isGuard(state: Pick<GameState, 'modeState' | 'config'>, player: Pick<PlayerState, 'teamIndex'>): boolean {
  if (!alertLayerActive(state)) return false;
  const teams = state.config.modeConfig.guardTeams;
  return !teams || teams.includes(player.teamIndex);
}

/**
 * WORKBOARD decisions 12 / 13 — two observers through one observer kind. A **vehicle** (layer 2, the turn match:
 * today's unit re-read as a crewed walker with sensors) perceives at the constants; a **person** (layers 0 / 1,
 * on foot) perceives at the same ranges × the scale profile's k_human. One alert ladder serves both.
 */
export function observerScale(observer: Observer, scale: ScaleProfileName | undefined): number {
  return observer === 'person' ? scaleProfile(scale).kHuman : 1;
}

export function perceptionRange(level: AlertLevel, observer: Observer = 'vehicle', scale?: ScaleProfileName): number {
  const base = level === 'alert' ? Infinity : level === 'suspicious' ? PERCEPTION_RANGE_SUSPICIOUS : PERCEPTION_RANGE_UNAWARE;
  return Number.isFinite(base) ? base * observerScale(observer, scale) : base;
}

/** A sighting closer than this goes straight to alert — × k_human for a person. */
export function perceptionCloseRange(observer: Observer = 'vehicle', scale?: ScaleProfileName): number {
  return PERCEPTION_CLOSE_RANGE * observerScale(observer, scale);
}

/** Phase 57.9: the distance `viewer` perceives `target` at — the level's range × the viewer's sensors × the target's camo. */
export function perceptionRangeFor(level: AlertLevel, viewer: Pick<PlayerState, 'modifiers'>, target: Pick<PlayerState, 'modifiers'>, observer: Observer = 'vehicle', scale?: ScaleProfileName): number {
  const base = perceptionRange(level, observer, scale);
  if (!Number.isFinite(base)) return base;
  return base * (viewer.modifiers?.sensorMult ?? 1) * (target.modifiers?.stealthMult ?? 1);
}

/** Unit tangent from `from` toward `to` in the tangent plane at `from`'s surface normal (null when degenerate). */
export function tangentToward(normal: Vec3, from: Vec3, to: Vec3): Vec3 | null {
  const n = vecNormalize(normal);
  const d = vecSub(to, from);
  const t = vecSub(d, vecScale(n, vecDot(d, n)));
  const len = vecLength(t);
  if (len < 1e-6) return null;
  return vecScale(t, 1 / len);
}

/**
 * Which of `candidateIds` (already past the terrain line-of-sight rule) this
 * viewer perceives. Everything, unless the viewer is a guard on a planet that is
 * not alert; then only candidates within the level's range and, when the viewer
 * has a facing, inside its cone.
 */
export function perceptionFilter(state: GameState, viewer: PlayerState, candidateIds: number[], observer: Observer = observerOf(viewer)): number[] {
  if (candidateIds.length === 0 || !isGuard(state, viewer)) return candidateIds;
  const level = alertLevelAt(state, viewer.planetIndex);
  if (level === 'alert') return candidateIds;
  const scale = state.config.scale;
  const eyeH = eyeHeightFor(observer, scale);
  const eye = losEyePoint(viewer, state.planets, eyeH);
  const planet = state.planets[viewer.planetIndex];
  const normal = viewer.airborne ? null : vecNormalize(vecSub(eye, planet.position));
  const weatherMult = weatherEffectHere(state, viewer.planetIndex, viewer.surfaceDir).perception;   // 62.18 (1 without weather); 62.54: where the viewer is
  const out: number[] = [];
  for (const id of candidateIds) {
    const target = state.players.find(p => p.id === id);
    if (!target) continue;
    const tEye = losEyePoint(target, state.planets, eyeH);
    const dist = vecLength(vecSub(tEye, eye));
    if (dist > perceptionRangeFor(level, viewer, target, observer, scale) * weatherMult) continue;
    if (viewer.facing && normal) {
      const toward = tangentToward(normal, eye, tEye);
      if (toward) {
        const cos = vecDot(vecNormalize(viewer.facing), toward);
        if (cos < dcos(PERCEPTION_CONE_HALF_ANGLE)) continue;
      }
    }
    out.push(id);
  }
  return out;
}

export interface AlertChange { planetIndex: number; from: AlertLevel; to: AlertLevel; cause: string }

/** Raise a planet to at least `level` (never lowers). */
export function raiseAlert(
  state: GameState, planetIndex: number, level: AlertLevel, turn: number, cause: string,
  seen?: { playerId: number; surfaceDir: Vec3 },
): { state: GameState; change: AlertChange | null } {
  const a = state.modeState.alert;
  if (!a) return { state, change: null };
  const cur = a.byPlanet[planetIndex];
  if (!cur) return { state, change: null };
  const lastSeen = seen ? { playerId: seen.playerId, surfaceDir: { ...seen.surfaceDir }, turn } : cur.lastSeen;
  if (LEVEL_RANK[level] <= LEVEL_RANK[cur.level]) {
    if (!seen) return { state, change: null };
    const byPlanet = { ...a.byPlanet, [planetIndex]: { ...cur, since: turn, lastSeen } };
    return { state: { ...state, modeState: { ...state.modeState, alert: { ...a, byPlanet } } }, change: null };
  }
  const byPlanet = { ...a.byPlanet, [planetIndex]: { level, since: turn, lastSeen } };
  const wentLoud = a.wentLoud || level === 'alert';
  const next: AlertState = { ...a, byPlanet, wentLoud, ...(wentLoud && a.wentLoudTurn === undefined && level === 'alert' ? { wentLoudTurn: turn } : {}) };
  return { state: { ...state, modeState: { ...state.modeState, alert: next } }, change: { planetIndex, from: cur.level, to: level, cause } };
}

export type Loudness = 'loud' | 'quiet' | 'silent';

/**
 * Noise at a point: 'loud' (a projectile impact) alerts the planet it hit and
 * makes every planet within earshot suspicious; 'quiet' (a beam) makes the
 * shooter's planet suspicious; 'silent' (a takedown) does nothing.
 */
export function noise(
  state: GameState, at: { point: Vec3; planetIndex: number; loudness: Loudness }, turn: number,
): { state: GameState; changes: AlertChange[] } {
  if (!alertLayerActive(state) || at.loudness === 'silent') return { state, changes: [] };
  const changes: AlertChange[] = [];
  let s = state;
  if (at.loudness === 'loud') {
    if (at.planetIndex >= 0) { const r = raiseAlert(s, at.planetIndex, 'alert', turn, 'impact'); s = r.state; if (r.change) changes.push(r.change); }
    for (let i = 0; i < s.planets.length; i++) {
      const p = s.planets[i];
      if (i === at.planetIndex || !p.alive || p.type !== 'planet') continue;
      if (vecLength(vecSub(p.position, at.point)) - p.radius <= NOISE_RADIUS_SHOT) {
        const r = raiseAlert(s, i, 'suspicious', turn, 'heard a shot'); s = r.state; if (r.change) changes.push(r.change);
      }
    }
  } else if (at.planetIndex >= 0) {
    const r = raiseAlert(s, at.planetIndex, 'suspicious', turn, 'beam'); s = r.state; if (r.change) changes.push(r.change);
  }
  return { state: s, changes };
}

/**
 * Every guard looks: an enemy it perceives (terrain line of sight, then range
 * and cone) raises its planet — alert when close, suspicious otherwise —
 * remembering where the enemy stood.
 */
export function observe(state: GameState, turn: number): { state: GameState; changes: AlertChange[] } {
  if (!alertLayerActive(state)) return { state, changes: [] };
  const changes: AlertChange[] = [];
  let s = state;
  for (const guard of s.players) {
    if (!guard.alive || guard.airborne || !isGuard(s, guard)) continue;
    if (alertLevelAt(s, guard.planetIndex) === 'alert') continue;
    const eye = losEyePoint(guard, s.planets);
    const seen: number[] = [];
    for (const e of s.players) {
      if (!e.alive || e.teamIndex === guard.teamIndex) continue;
      if (hasLineOfSight(eye, losEyePoint(e, s.planets), s.planets)) seen.push(e.id);
    }
    for (const id of perceptionFilter(s, guard, seen)) {
      const enemy = s.players.find(p => p.id === id)!;
      const dist = vecLength(vecSub(losEyePoint(enemy, s.planets), eye));
      const level: AlertLevel = dist <= PERCEPTION_CLOSE_RANGE ? 'alert' : 'suspicious';
      const r = raiseAlert(s, guard.planetIndex, level, turn, `${guard.name} saw ${enemy.name}`, { playerId: enemy.id, surfaceDir: enemy.surfaceDir });
      s = r.state; if (r.change) changes.push(r.change);
    }
  }
  return { state: s, changes };
}

/**
 * Evidence (57.7a): what someone else did on a planet that a guard standing there
 * would notice — collected nodes, enemy stakes, enemy claims or interactions on its
 * ruins, its own team's cores not at rest at home. A count; a rise since the last
 * acknowledgement is "someone's been here".
 */
export function evidenceSignature(state: GameState, planetIndex: number, guardTeam: number): number {
  let n = 0;
  const byId = new Map(state.players.map(p => [p.id, p]));
  for (const c of state.collectibles) {
    if (c.planetIndex !== planetIndex || !c.collected) continue;
    const taker = c.collectedBy === null ? undefined : byId.get(c.collectedBy);
    if (taker && taker.teamIndex === guardTeam) continue;   // the garrison's own work is not evidence
    n++;
  }
  for (const f of state.plantedFlags) if (f.planetIndex === planetIndex && f.teamIndex !== guardTeam && f.alive !== false) n++;
  for (const r of state.ruins) {
    if (r.planetIndex !== planetIndex) continue;
    if (r.claimTeamIndex !== undefined && r.claimTeamIndex >= 0 && r.claimTeamIndex !== guardTeam) n++;
  }
  for (const ip of state.modeState.interactionProgress ?? []) {
    if (ip.teamIndex === guardTeam) continue;
    const ruin = state.ruins.find(r => r.id === ip.ruinId);
    if (ruin && ruin.planetIndex === planetIndex) n++;
  }
  for (const c of state.carryables) {
    if (!c.alive || c.teamIndex !== guardTeam) continue;
    const home = c.sourcePlanetIndex ?? (state.config.modeConfig.homeTeamPlanets?.[c.teamIndex]);
    if (home !== planetIndex) continue;
    if (c.carriedBy !== null || c.planetIndex !== home) n++;
  }
  return n;
}

/** Guards standing on a planet notice evidence: the first find makes it suspicious, a find on a suspicious planet alerts it. */
export function noticeEvidence(state: GameState, turn: number): { state: GameState; changes: AlertChange[] } {
  const a = state.modeState.alert;
  if (!a) return { state, changes: [] };
  const changes: AlertChange[] = [];
  let s = state;
  for (const key of Object.keys(a.byPlanet)) {
    const i = Number(key);
    const guard = s.players.find(p => p.alive && !p.airborne && p.planetIndex === i && isGuard(s, p));
    if (!guard) continue;
    const cur = s.modeState.alert!.byPlanet[i];
    const sig = evidenceSignature(s, i, guard.teamIndex);
    const seen = cur.evidenceSeen ?? 0;
    if (sig <= seen) continue;
    const level: AlertLevel = cur.level === 'unaware' ? 'suspicious' : 'alert';
    const r = raiseAlert(s, i, level, turn, 'evidence');
    s = r.state; if (r.change) changes.push(r.change);
    const byPlanet = { ...s.modeState.alert!.byPlanet, [i]: { ...s.modeState.alert!.byPlanet[i], evidenceSeen: sig, since: turn } };
    s = { ...s, modeState: { ...s.modeState, alert: { ...s.modeState.alert!, byPlanet } } };
  }
  return { state: s, changes };
}

/** Decay by turns without new cause: suspicious → unaware, alert → suspicious. */
export function tickAlert(state: GameState, turn: number): { state: GameState; changes: AlertChange[] } {
  const a = state.modeState.alert;
  if (!a) return { state, changes: [] };
  const changes: AlertChange[] = [];
  const byPlanet = { ...a.byPlanet };
  for (const key of Object.keys(byPlanet)) {
    const i = Number(key);
    const p = byPlanet[i];
    if (p.level === 'suspicious' && turn - p.since >= ALERT_DECAY_TURNS_SUSPICIOUS) { byPlanet[i] = { ...p, level: 'unaware', since: turn }; changes.push({ planetIndex: i, from: 'suspicious', to: 'unaware', cause: 'quiet' }); }
    else if (p.level === 'alert' && turn - p.since >= ALERT_DECAY_TURNS_ALERT) { byPlanet[i] = { ...p, level: 'suspicious', since: turn }; changes.push({ planetIndex: i, from: 'alert', to: 'suspicious', cause: 'stood down' }); }
  }
  if (changes.length === 0) return { state, changes };
  return { state: { ...state, modeState: { ...state.modeState, alert: { ...a, byPlanet } } }, changes };
}

/** Facing for a unit that walked from `fromDir` toward `toDir` on `planet`, or fired along `azimuth` in its tangent frame. */
export function facingAfterWalk(planet: PlanetState, fromDir: Vec3, toDir: Vec3): Vec3 | undefined {
  const n = vecNormalize(fromDir);
  const t = tangentToward(n, vecScale(n, planet.radius), vecScale(vecNormalize(toDir), planet.radius));
  return t ?? undefined;
}

export function describeChange(c: AlertChange, planets: PlanetState[]): string {
  const name = `Planet ${c.planetIndex}`;
  void planets;
  return c.to === 'alert' ? `${name} is on alert (${c.cause})`
    : c.to === 'suspicious' ? (c.from === 'alert' ? `${name} stands down to suspicious` : c.cause === 'evidence' ? `${name}: someone has been here` : `${name} grows suspicious (${c.cause})`)
    : `${name} settles`;
}

/** The unit tangent a shot along `azimuth` leaves in (the bots' and the HUD's azimuth convention: atan2(ty, tx) in surfaceTangentFrame). */
export function facingFromAzimuth(player: PlayerState, azimuth: number): Vec3 {
  const n = player.surfaceDir;
  const len = dhypot(n.x, n.y, n.z) || 1;
  const { tx, ty } = surfaceTangentFrame({ x: n.x / len, y: n.y / len, z: n.z / len });
  const c = dcos(azimuth), sn = dsin(azimuth);
  return { x: tx.x * c + ty.x * sn, y: tx.y * c + ty.y * sn, z: tx.z * c + ty.z * sn };
}

// ---------------------------------------------------------------------------
// 62.54 step 6: the fog along the line of sight (behind weatherCells)
// ---------------------------------------------------------------------------

/**
 * How much weather a line of sight from `from` to `to` (world points over planet `planetIndex`) passes through, as a
 * share of what blinds: each stretch of the line counts its length over the sight where it lies (WEATHER_EFFECTS sight
 * × the person scale; the cells' blend across their edges), summed at a fixed step (WEATHER_CELLS.sightStepPeople, at
 * most sightSamples, the midpoints of equal stretches), each sample keyed to its cell of that size on the planet (the
 * weather at the cell's centre at the whole second, as the wind grid reads it, kept for that second: a second's looks share
 * their samples). In one weather everywhere it is distance ÷ sight — today's rule;
 * a fog bank between two units counts the stretch inside it, whoever stands where. Only cells whose edge reaches the
 * line are sampled: a line far from every cell is 0 (clear). Pure: dmath, the cells' closed form.
 */
export function weatherDepth(state: GameState, planetIndex: number, from: Vec3, to: Vec3, seconds: number): number {
  const planet = state.planets[planetIndex];
  if (!planet) return 0;
  const ps = personSetOf(state);
  const person = PERSON_BODY_HEIGHT * ps.k;
  const len = vecLength(vecSub(to, from));
  if (len < 1e-12) return 0;
  // the cells that can reach the line: within their edge of its middle, with the line's half-length as slack
  const mid = vecNormalize(vecSub(vecScale({ x: from.x + to.x, y: from.y + to.y, z: from.z + to.z }, 0.5), planet.position));
  const slack = (len * 0.5) / planet.radius;
  let near = false;
  for (const c of cellsAt(state, planetIndex, weatherTime(state, gridSecond(seconds)))) {
    if (WEATHER_EFFECTS[c.kind].sight === Infinity) continue;   // a clear high blinds nothing
    const reach = WEATHER_CELLS.edge * c.radius + slack;
    if (vecDot(c.centre, mid) >= dcos(Math.min(Math.PI, reach))) { near = true; break; }
  }
  if (!near) return 0;
  const n = Math.max(1, Math.min(WEATHER_CELLS.sightSamples, Math.ceil(len / (WEATHER_CELLS.sightStepPeople * person))));
  const piece = len / n;
  const memo = fogMemoFor(state, planetIndex, seconds, person);
  let depth = 0;
  for (let i = 0; i < n; i++) {
    const t = (i + 0.5) / n;
    const x = from.x + (to.x - from.x) * t - planet.position.x, y = from.y + (to.y - from.y) * t - planet.position.y, z = from.z + (to.z - from.z) * t - planet.position.z;
    // keyed: the weather a sample reads is its cell's (~WEATHER_CELLS.sightStepPeople across), taken at the cell's centre and
    // kept for this second — the lines of one look share their samples, and no value depends on which line asked first
    const cell = cellOf(x, y, z, memo.n);
    let inv = memo.cells.get(cell);
    if (inv === undefined) {
      const sight = cellWeatherEffect(state, planetIndex, cellCentre(cell, memo.n), memo.at).sight * ps.k;
      inv = sight < Infinity ? 1 / sight : 0;
      memo.cells.set(cell, inv);
      simWork.fogSamples++;
    }
    depth += piece * inv;
  }
  return depth;
}

/** The fog samples of one second on one planet, by cell (weatherDepth's memo; a new second starts a new one). */
let fogMemo: { key: string; at: number; hm: Float32Array; planet: unknown; config: unknown; person: number; flora: unknown; n: number; cells: Map<number, number> } | null = null;
function fogMemoFor(state: GameState, planetIndex: number, seconds: number, person: number): { at: number; n: number; cells: Map<number, number> } {
  const planet = state.planets[planetIndex];
  const at = gridSecond(seconds);   // the weather's second (the wind grid's): a second's ticks share the samples
  // 62.57 cut 5: the same second, planet object and config as the last call (a look's lines) — no key built
  if (fogMemo && fogMemo.at === at && fogMemo.planet === planet && fogMemo.config === state.config && fogMemo.person === person) return fogMemo;
  const key = `${state.config.seed}|${planet.id}|${planet.archetype}|${weatherTime(state, at)}|${person}|${state.config.weatherFixed ?? ''}`;
  // the ground (a crater moves where fog pools) by identity
  if (fogMemo && fogMemo.key === key && fogMemo.hm === planet.heightmap) { fogMemo = { ...fogMemo, at, planet, config: state.config, person }; return fogMemo; }
  fogMemo = { key, at, hm: planet.heightmap, planet, config: state.config, person, flora: null, n: Math.max(8, Math.ceil((2 * planet.radius) / (WEATHER_CELLS.sightStepPeople * person))), cells: new Map() };
  return fogMemo;
}

/**
 * 62.54 step 6: does the weather let `from` see `to`? With weatherCells on, the fog along the line (weatherDepth ≤ 1):
 * a bank between two units cuts sight both ways, and a viewer inside one sees out once the bank's part of the line is
 * within its sight. Off (or across planets), today's rule: the distance within `viewerSight` (the viewer's weather sight,
 * person scale — what the caller compared before). The terrain line of sight is the caller's, after this.
 */
export function weatherSees(state: GameState, planetIndex: number, from: Vec3, to: Vec3, seconds: number, viewerSight: number, toPlanet = planetIndex): boolean {
  if (!state.config.weather || !state.config.weatherCells || state.config.weatherFixed || toPlanet !== planetIndex) return vecLength(vecSub(to, from)) <= viewerSight;
  return weatherDepth(state, planetIndex, from, to, seconds) <= 1;
}

